import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Statistic, Typography, Space, Button, Table,
  Tag, message, Divider, Progress, Skeleton, Alert,
} from "antd";
import {
  DownloadOutlined, ReloadOutlined, BarChartOutlined,
  RiseOutlined, ProjectOutlined, ThunderboltOutlined,
} from "@ant-design/icons";
import { reportingApi } from "../api/client";
import dayjs from "dayjs";

const { Title, Text } = Typography;

// ── Donut-style RAG widget ────────────────────────────────────────────────────
function RAGWidget({ verde, ambar, rojo }: { verde: number; ambar: number; rojo: number }) {
  const total = verde + ambar + rojo || 1;
  const items = [
    { label: "Verde", value: verde, color: "#52c41a" },
    { label: "Ámbar", value: ambar, color: "#faad14" },
    { label: "Rojo", value: rojo, color: "#ff4d4f" },
  ];
  return (
    <Row gutter={12}>
      {items.map(({ label, value, color }) => (
        <Col key={label} span={8}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
            <Progress
              percent={Math.round((value / total) * 100)}
              showInfo={false}
              strokeColor={color}
              trailColor="#f0f0f0"
              size="small"
            />
            <Text style={{ fontSize: 12, color: "#888" }}>{label}</Text>
          </div>
        </Col>
      ))}
    </Row>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ReportingPage() {
  const [reporte, setReporte] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await reportingApi.semanal();
      setReporte(res.data);
    } catch { message.error("Error cargando reporte"); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const handleDescargarPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await reportingApi.semanalPdf();
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_semanal_aao_${dayjs().format("YYYY-MM-DD")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { message.error("Error generando PDF"); }
    finally { setPdfLoading(false); }
  };

  if (loading || !reporte) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  const { kpis_generales: kpis, periodo, quick_wins_semana, retorno_acumulado, proyectos_ia, oportunidades_criticas } = reporte;
  const rag = kpis?.proyectos_rag ?? {};

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>Reporte Ejecutivo Semanal</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Semana {dayjs(periodo.semana_inicio).format("DD/MM")} –{" "}
              {dayjs(periodo.semana_fin).format("DD/MM/YYYY")} · Generado:{" "}
              {dayjs(periodo.generado_en).format("DD/MM HH:mm")}
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={cargar} loading={loading}>
              Actualizar
            </Button>
            <Button
              type="primary" icon={<DownloadOutlined />}
              loading={pdfLoading} onClick={handleDescargarPDF}
              style={{ background: "#1a1f5e" }}
            >
              Descargar PDF
            </Button>
          </Space>
        </Space>
      </div>

      {/* KPIs principales */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {[
          { label: "Solicitudes activas", value: kpis.total_solicitudes_activas, icon: <ThunderboltOutlined /> },
          { label: "Proyectos activos", value: kpis.total_proyectos_activos, icon: <ProjectOutlined /> },
          { label: "QW entregados semana", value: kpis.quick_wins_semana, icon: <RiseOutlined /> },
          { label: "Oportunidades activas", value: kpis.oportunidades_activas, icon: <BarChartOutlined /> },
        ].map(({ label, value, icon }) => (
          <Col key={label} xs={12} lg={6}>
            <Card size="small" style={{ borderLeft: "3px solid #1a1f5e" }}>
              <Statistic
                title={<Text style={{ fontSize: 12 }}>{label}</Text>}
                value={value}
                prefix={React.cloneElement(icon, { style: { color: "#1a1f5e" } })}
                valueStyle={{ fontSize: 24, fontWeight: 700, color: "#1a1f5e" }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* RAG */}
        <Col xs={24} md={8}>
          <Card size="small" title={<Text strong>Estado RAG · Proyectos</Text>} style={{ height: "100%" }}>
            <RAGWidget verde={rag["Verde"] ?? 0} ambar={rag["Ámbar"] ?? 0} rojo={rag["Rojo"] ?? 0} />
          </Card>
        </Col>

        {/* Retorno acumulado */}
        <Col xs={24} md={16}>
          <Card size="small" title={<Text strong>Retorno acumulado del portfolio</Text>} style={{ height: "100%" }}>
            <Row gutter={24}>
              {[
                { label: "ROI promedio", value: retorno_acumulado?.roi_promedio != null ? `${retorno_acumulado.roi_promedio.toFixed(1)}%` : "—", color: "#52c41a" },
                { label: "Beneficio anual estimado", value: retorno_acumulado?.beneficio_total_anual != null ? `€${Math.round(retorno_acumulado.beneficio_total_anual).toLocaleString()}` : "—", color: "#1a1f5e" },
                { label: "Inversión total", value: retorno_acumulado?.coste_total_implementacion != null ? `€${Math.round(retorno_acumulado.coste_total_implementacion).toLocaleString()}` : "—", color: "#faad14" },
              ].map(({ label, value, color }) => (
                <Col key={label} xs={24} sm={8} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
                  <Text style={{ fontSize: 12, color: "#888" }}>{label}</Text>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Quick Wins semana */}
      <Card size="small" title={<Text strong>Quick Wins entregados esta semana</Text>} style={{ marginTop: 16 }}>
        {!quick_wins_semana?.length ? (
          <Text type="secondary">Sin quick wins entregados esta semana.</Text>
        ) : (
          <Table
            dataSource={quick_wins_semana}
            rowKey="qw_id"
            size="small"
            pagination={false}
            columns={[
              { title: "ID", dataIndex: "qw_id", width: 90, render: (v: string) => <Text code>{v}</Text> },
              { title: "Título", dataIndex: "titulo", ellipsis: true },
              { title: "Área", dataIndex: "area", width: 160 },
              { title: "Replicable", dataIndex: "replicable", width: 90, render: (v: boolean) => v ? <Tag color="green">Sí</Tag> : null },
            ]}
          />
        )}
      </Card>

      {/* Proyectos IA */}
      {proyectos_ia?.length > 0 && (
        <Card size="small" title={<Text strong>Proyectos con IA</Text>} style={{ marginTop: 16 }}>
          <Table
            dataSource={proyectos_ia}
            rowKey="proj_id"
            size="small"
            pagination={false}
            columns={[
              { title: "ID", dataIndex: "proj_id", width: 100, render: (v: string) => <Text code>{v}</Text> },
              { title: "Nombre", dataIndex: "nombre", ellipsis: true },
              { title: "Tecnología", dataIndex: "tipo_tecnologia", width: 160, render: (v: string) => v ? <Tag color="purple">{v}</Tag> : null },
              { title: "Madurez", dataIndex: "nivel_madurez", width: 120, render: (v: string) => v ? <Tag>{v}</Tag> : null },
              { title: "CAI", dataIndex: "estado_cai", width: 110, render: (v: string) => <Tag color={v === "Aprobado" ? "green" : v === "Pendiente" ? "orange" : "default"}>{v}</Tag> },
            ]}
          />
        </Card>
      )}

      {/* Oportunidades críticas */}
      {oportunidades_criticas?.length > 0 && (
        <Card size="small" title={<Text strong>Oportunidades críticas</Text>} style={{ marginTop: 16 }}>
          <Table
            dataSource={oportunidades_criticas}
            rowKey="opp_id"
            size="small"
            pagination={false}
            columns={[
              { title: "ID", dataIndex: "opp_id", width: 90, render: (v: string) => <Text code>{v}</Text> },
              { title: "Nombre", dataIndex: "nombre", ellipsis: true },
              { title: "Límite", dataIndex: "fecha_limite", width: 100, render: (v: string) => v ? dayjs(v).format("DD/MM/YYYY") : "—" },
              { title: "Alerta", dataIndex: "alerta", width: 70, render: (v: boolean) => v ? <Tag color="red">⚠</Tag> : null },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
